import { useState, useEffect } from 'react';
import io from 'socket.io-client';
import axiosExpress from '../services/axiosExpress';
import Loader from './Loader';
import Select from 'react-select';
import axiosDotnet from '../services/axiosDotnet';
import EditTaskModal from "./EditTaskModal";

const socket = io('http://localhost:7070');

export default function KanbanBoard({ projectId, workspaceId, setSelectedProjectForTask, setShowCreateTaskModal }) {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const columns = [
        { id: 'TODO', title: 'To Do', color: 'bg-blue-500' },
        { id: 'IN_PROGRESS', title: 'In Progress', color: 'bg-amber-500' },
        { id: 'DONE', title: 'Done', color: 'bg-emerald-500' }
    ];

    const filterOptions = [
        { value: "TODO", label: "To Do" },
        { value: "IN_PROGRESS", label: "In Progress" },
        { value: "DONE", label: "Done" }
    ];

    useEffect(() => {
        fetchTasks();

        socket.emit('joinProject', { projectId });
        socket.emit('joinWorkspace', { workspaceId });

        socket.on('task:created', (newTask) => {
            if (newTask.projectId === projectId || newTask.ProjectId === projectId) {
                setTasks(prev => {
                    const exists = prev.some(t => (t.id || t.Id) === (newTask.id || newTask.Id));
                    return exists ? prev : [...prev, newTask];
                });
            }
        });

        socket.on('task:updated', (updatedTask) => {
            setTasks(prev => prev.map(t =>
                (t.id || t.Id) === (updatedTask.id || updatedTask.Id) ? updatedTask : t
            ));
        });

        return () => {
            socket.off('task:created');
            socket.off('task:updated');
        };
    }, [projectId]);

    const fetchTasks = async () => {
        try {
            // console.log(projectId)
            const res = await axiosExpress.get(`/api/kanban/project/${projectId}`);
            // console.log(res?.data)
            setTasks(res?.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const updateTaskStatus = async (taskId, newStatus) => {
        const task = tasks.find(t => (t.id || t.Id) === taskId);
        if (!task || (task.status || task.Status) === newStatus) return;

        try {
            // Optimistic update
            setTasks(prev => prev.map(t =>
                (t.id || t.Id) === taskId ? { ...t, status: newStatus, Status: newStatus } : t
            ));

            await axiosExpress.put(`/api/kanban/task/${taskId}`, {
                status: newStatus,
                order: task.order || task.Order || 0,
                assignedTo: task.assignedTo || task.AssignedTo || null,
                assignedUserName: task.assignedUserName || task.AssignedUserName || null
            });

        } catch (err) {
            console.error("Failed to update status", err);
            fetchTasks(); // Revert
        }
    };

    // Delete Task
    const handleDeleteTask = async (taskId, taskTitle) => {
        if (!window.confirm(`Delete task "${taskTitle}"?`)) return;

        try {
            await axiosDotnet.delete(`/api/tasks/${taskId}`);   // .NET endpoint
            setTasks(prev => prev.filter(t => (t.id || t.Id) !== taskId));
            showSuccess("Task deleted successfully");
        } catch (err) {
            console.error(err);
            showError("Failed to delete task");
            fetchTasks();
        }
    };

    const handleUpdateTask = (task) => {
        setEditingTask(task);
        setShowEditModal(true);
    };

    // Helper to update task (title, description, etc.)
    const updateTask = async (taskId, updates) => {
        try {
            await axiosDotnet.put(`/api/tasks/${taskId}`, updates);
            fetchTasks(); // Refresh
            showSuccess("Task updated");
        } catch (err) {
            showError("Failed to update task");
        }
    };

    // Handle Drag & Drop
    const handleDragStart = (e, taskId) => {
        e.dataTransfer.setData('taskId', taskId);
    };

    const handleDrop = async (e, newStatus) => {
        e.preventDefault();
        const taskId = e.dataTransfer.getData('taskId');
        updateTaskStatus(taskId, newStatus);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    if (loading) return <Loader text="Loading Tasks Kanban Board..." />

    return (
        <div className="p-8">
            <div className="flex justify-between items-center mb-8">
                <h2 className="text-3xl font-bold">Project Kanban Board</h2>
                <h4 className="text-xl">{tasks?.length ? tasks?.length : "No"} Tasks</h4>
                <div className="flex items-center gap-2 text-sm bg-zinc-900 px-5 py-2 rounded-3xl border border-zinc-700">
                    AI auto-prioritization enabled
                    <span className="text-emerald-400">●</span>
                </div>
            </div>

            <div className="flex gap-6 overflow-x-auto pb-8">
                {columns.map((column) => (
                    <div
                        key={column.id}
                        className="min-w-[340px] h-[100vh] bg-zinc-900 border border-zinc-800 rounded-3xl p-5 flex flex-col overflow-hidden"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, column.id)}
                    >
                        {/* Column Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className={`w-3 h-3 rounded-full ${column.color}`}></div>
                                <h4 className="font-semibold text-lg">{column.title}</h4>
                            </div>
                            <div className="bg-zinc-800 text-zinc-400 text-xs px-3 py-1 rounded-3xl">
                                {tasks?.filter(t => t.Status === column.id).length}
                            </div>
                        </div>

                        {/* Tasks */}
                        <div className="flex-1 overflow-y-auto pr-3 space-y-4 custom-scrollbar">
                            {tasks
                                .filter(task => task.Status === column.id)
                                .map((task) => {
                                    console.log(task.Id)
                                    const taskId = task.id || task.Id;
                                    return (
                                        <div

                                            key={taskId}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, task.Id)}
                                            className="bg-zinc-800 rounded-3xl p-5 hover:shadow-xl transition-all cursor-grab active:cursor-grabbing"
                                        >
                                            <div className="font-medium leading-tight">{task.Title}</div>

                                            {task.Description && (
                                                <p className="text-sm text-zinc-400 mt-3 ">
                                                    {/* line-clamp-2 */}
                                                    {task.Description}
                                                </p>
                                            )}

                                            <Select
                                                value={filterOptions.find(option => option.value === task.Status)}
                                                onChange={(selected) => updateTaskStatus(taskId, selected.value)}
                                                options={filterOptions}
                                                className="mt-3 w-full"
                                                classNamePrefix="status-select"
                                                isSearchable={false}
                                                theme={(theme) => ({
                                                    ...theme,
                                                    colors: {
                                                        ...theme.colors,
                                                        primary: '#8b5cf6',
                                                        primary25: '#4c1d95',
                                                        neutral0: '#18181b',
                                                        neutral10: '#27272a',
                                                        neutral80: '#ffffff',
                                                    },
                                                })}
                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        backgroundColor: '#18181b',
                                                        borderColor: '#3f3f46',
                                                        color: '#ffffff',
                                                        borderRadius: '12px',
                                                        minHeight: '38px',
                                                        boxShadow: 'none',
                                                        '&:hover': {
                                                            borderColor: '#6b7280',
                                                        },
                                                        cursor: "pointer"
                                                    }),
                                                    singleValue: (base) => ({
                                                        ...base,
                                                        color: '#ffffff',
                                                        fontSize: '13px',
                                                    }),
                                                    option: (base, state) => ({
                                                        ...base,
                                                        backgroundColor: state.isSelected ? '#4c1d95' : '#18181b',
                                                        color: '#ffffff',
                                                        fontSize: '13px',
                                                        '&:hover': {
                                                            backgroundColor: '#27272a',
                                                        },
                                                        cursor: "pointer"
                                                    }),
                                                    menu: (base) => ({
                                                        ...base,
                                                        backgroundColor: '#18181b',
                                                        border: '1px solid #3f3f46',
                                                        borderRadius: '12px',
                                                        marginTop: '4px',
                                                    }),
                                                }}
                                            />


                                            <div className="flex justify-between mt-6">
                                                {task.AssignedUserName && (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-7 h-7 bg-violet-900 text-white text-[10px] font-bold flex items-center justify-center rounded-2xl">
                                                            {task.AssignedUserName.substring(0, 2).toUpperCase()}
                                                        </div>
                                                        <span className="text-xs text-zinc-400">{task.AssignedUserName}</span>
                                                    </div>
                                                )}

                                                {task.DueDate && (
                                                    <span className="text-xs text-zinc-400">
                                                        due {new Date(task.DueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                    </span>
                                                )}
                                            </div>
                                            {/* Update & Delete Buttons */}
                                            <div className="flex gap-2 mt-6 pt-4 border-t border-zinc-700">
                                                <button
                                                    onClick={() => handleUpdateTask(task)}
                                                    className="flex-1 cursor-pointer py-2 text-xs border border-zinc-600 hover:bg-zinc-800 rounded-xl transition-all"
                                                >
                                                    ✏️ Update
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteTask(taskId, task.Title || task.title)}
                                                    className="flex-1 cursor-pointer py-2 text-xs border border-red-600 hover:bg-red-900/30 text-red-400 rounded-xl transition-all"
                                                >
                                                    🗑️ Delete
                                                </button>
                                            </div>
                                        </div>
                                    )
                                }
                                )}
                        </div>


                    </div>
                )

                )}
            </div>
            {showEditModal && editingTask && (
                <EditTaskModal
                    task={editingTask}
                    isOpen={showEditModal}
                    onClose={() => setShowEditModal(false)}
                    onTaskUpdated={fetchTasks}
                />
            )}
        </div>
    );
}

{/* Add Task Button */ }
{/* <button 
                        onClick={() => {
                            setSelectedProjectForTask(projectId);
                            setShowCreateTaskModal(true);
                        }}
                        className="mt-6 w-full py-4 text-sm font-medium border border-dashed border-zinc-600 rounded-3xl hover:border-violet-400 transition-colors">
                            + Add task
                        </button> */}



// ==============================================================================================================================>
